import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Divider,
  InputAdornment,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  MenuBook as MenuIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  RestaurantMenu as RecipeIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Tune as TuneIcon,
  LocalOffer as OptionIcon,
  Star as StarIcon,
  InfoOutlined as InfoOutlinedIcon,
  BookmarkAdd as BookmarkAddIcon,
  Bookmarks as BookmarksIcon,
  ContentPaste as ContentPasteIcon
} from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';
import type { InventoryItem, Warehouse, InventoryCategory } from '../types/inventory.types';
import { accountingApi } from '../../../../config/apiClient';
import InventoryProductSelectionModal from './InventoryProductSelectionModal';
import {
  loadMenuTemplates,
  saveMenuTemplate,
  deleteMenuTemplate,
  countTemplateRows,
  type MenuTemplate,
} from '../utils/menuTemplates';

// Types
interface ProductMenu {
  id: string;
  companyId: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  menuName: string;
  menuCode?: string;
  description?: string;
  allowNegativeStock: boolean;
  isActive: boolean;
  createdAt: string;
  menuItems?: ProductMenuItem[];
}

interface ProductMenuItem {
  id: string;
  companyId: string;
  menuId: string;
  itemId: string;
  itemName?: string;
  itemBarcode?: string;
  itemStockCode?: string;
  quantity: number;
  unit: string;
  wastagePercent: number;
  canBeNegative: boolean;
  hasRecipe: boolean;
  sortOrder: number;
  notes?: string;
  isActive: boolean;
  // Opsiyon alanları
  isOptional?: boolean;
  optionGroupId?: string;
  optionGroupName?: string;
  isDefaultOption?: boolean;
  extraOptions?: ExtraOption[];
}

interface ExtraOption {
  id: string;
  companyId: string;
  menuItemId: string;
  optionItemId: string;
  optionItemName?: string;
  optionItemUnit?: string;
  priceDifference: number;
  sortOrder: number;
  isActive: boolean;
}

interface ProductMenuModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  inventoryItems: InventoryItem[];
  companyId: string;
  onSuccess?: () => void;
  // ✅ Ürün seçim modalı filtreleri için (depo/kategori)
  warehouses?: Warehouse[];
  categories?: InventoryCategory[];
}

// ✅ Ürün seçim modalından seçilen, miktar/fire/not girilmeyi bekleyen ürün satırı
interface PendingMenuItem {
  item: InventoryItem;
  quantity: string;
  wastagePercent: string;
  notes: string;
}

interface EditingMenuItem {
  id: string;
  quantity: number;
  wastagePercent: number;
  notes?: string;
}

// ✅ Türkçe locale destekli decimal parse fonksiyonu
// Virgül (,) ve nokta (.) her ikisini de destekler
// Sonucu 4 ondalık haneye yuvarlar
const parseDecimalTR = (value: string | number): number => {
  if (typeof value === 'number') {
    return Math.round(value * 10000) / 10000; // 4 haneye yuvarla
  }
  if (!value || typeof value !== 'string') return 0;
  
  // Boşlukları temizle
  let cleaned = value.trim();
  
  // Türkçe format: 1.234,56 → 1234.56
  // İngilizce format: 1,234.56 → 1234.56
  
  // Hem nokta hem virgül varsa, hangisi son ise o ondalık ayıracı
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  
  if (lastComma > lastDot) {
    // Türkçe format: virgül ondalık ayıracı
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    // İngilizce format: nokta ondalık ayıracı
    cleaned = cleaned.replace(/,/g, '');
  } else if (lastComma !== -1) {
    // Sadece virgül var
    cleaned = cleaned.replace(',', '.');
  }
  // Sadece nokta varsa zaten doğru format
  
  const result = parseFloat(cleaned);
  if (isNaN(result)) return 0;
  
  // 4 ondalık haneye yuvarla (floating point hatalarını önler)
  return Math.round(result * 10000) / 10000;
};

export const ProductMenuModal: React.FC<ProductMenuModalProps> = ({
  open,
  onClose,
  item,
  inventoryItems,
  companyId,
  onSuccess,
  warehouses = [],
  categories = []
}) => {
  const { i18n } = useTranslation();
  // startsWith: "tr-TR" gibi bölgeli kodlarda da doğru çalışsın (bkz. src/i18n/index.ts)
  const isTr = !(i18n.language || 'tr').toLowerCase().startsWith('en');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  // State
  const [menu, setMenu] = useState<ProductMenu | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);

  // ✅ YENİ: Autocomplete için arama state'leri
  const [searchOptions, setSearchOptions] = useState<InventoryItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Menü bilgileri
  const [menuName, setMenuName] = useState('');
  const [menuCode, setMenuCode] = useState('');
  const [description, setDescription] = useState('');

  // ✅ YENİ: Ürün ekleme — Autocomplete yerine gelişmiş ürün seçim modalı.
  // Modaldan seçilen ürünler pendingItems listesine düşer; kullanıcı her satır
  // için miktar/fire/not girer ve toplu ekler.
  const [isProductSelectOpen, setIsProductSelectOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingMenuItem[]>([]);
  const [addingItem, setAddingItem] = useState(false);

  // Düzenleme
  const [editingMenuItem, setEditingMenuItem] = useState<EditingMenuItem | null>(null);

  // Menü çakışma kontrolü
  const [menuConflictError, setMenuConflictError] = useState<string | null>(null);

  // ✅ Silme onay dialog'ları için state'ler
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [menuItemToDelete, setMenuItemToDelete] = useState<string | null>(null);
  const [deleteMenuDialogOpen, setDeleteMenuDialogOpen] = useState(false);

  // ═══════════════════════════════════════════════════════════════
  // OPSİYONLU ÜRÜN VE EKSTRA OPSİYON STATE'LERİ
  // ═══════════════════════════════════════════════════════════════
  
  // 3 nokta menüsü
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuItemForAction, setSelectedMenuItemForAction] = useState<ProductMenuItem | null>(null);
  
  // Opsiyonlu ürün ekleme modalı
  const [showOptionalItemsModal, setShowOptionalItemsModal] = useState(false);
  const [optionGroupName, setOptionGroupName] = useState('');
  const [selectedOptionalItems, setSelectedOptionalItems] = useState<InventoryItem[]>([]);
  const [defaultOptionIndex, setDefaultOptionIndex] = useState<number>(0);
  const [addingOptionalItems, setAddingOptionalItems] = useState(false);
  // Opsiyonlu ürün seçimi de "Ürün Ekle" ile aynı gelişmiş modaldan yapılır;
  // düz arama kutusunda uzun listede ürün bulmak zordu.
  const [isOptionalProductSelectOpen, setIsOptionalProductSelectOpen] = useState(false);
  
  // Ekstra opsiyon ekleme modalı
  const [showExtraOptionsModal, setShowExtraOptionsModal] = useState(false);
  const [extraOptionItems, setExtraOptionItems] = useState<Array<{item: InventoryItem; priceDifference: string}>>([]);
  const [addingExtraOptions, setAddingExtraOptions] = useState(false);
  const [existingExtraOptions, setExistingExtraOptions] = useState<Array<{id: string; optionItemId: string; optionItemName?: string; optionItemUnit?: string; priceDifference: number}>>([]);
  const [deletingExtraOptionId, setDeletingExtraOptionId] = useState<string | null>(null);

  // ═══════════════════════════════════════════════════════════════
  // TASLAKLAR (localStorage) — aynı içerikli menüleri tek tek girmemek için
  // ═══════════════════════════════════════════════════════════════
  const [templates, setTemplates] = useState<MenuTemplate[]>([]);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateNameInput, setTemplateNameInput] = useState('');
  const [showApplyTemplateModal, setShowApplyTemplateModal] = useState(false);
  const [applyingTemplateId, setApplyingTemplateId] = useState<string | null>(null);
  /** Uygulama sonucu özeti: kaç satır eklendi / atlandı. */
  const [applyReport, setApplyReport] = useState<string | null>(null);

  // ✅ YENİ: Backend'den ürün arama fonksiyonu (Inventory.tsx ile aynı - POST kullanır)
  const searchProducts = async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setSearchOptions([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      // ✅ POST ile body göndermemiz gerekiyor (Backend böyle bekliyor)
      const response = await accountingApi.post(
        `/api/Inventory/items/paged`,
        {
          pageNumber: 1,
          pageSize: 50,
          searchTerm: searchTerm
        }
      );
      
      const data = response.data;
      const items = data.items || data.Items || [];
      
      // Mevcut ürünleri, kendisini ve menüleri hariç tut
      const existingIds = menu?.menuItems?.map(mi => mi.itemId) || [];
      
      const mappedItems: InventoryItem[] = items
        .map((apiItem: any) => ({
          id: apiItem.id || apiItem.Id || '',
          name: apiItem.name || apiItem.Name || '',
          stockCode: apiItem.stockCode || apiItem.StockCode || '',
          barcode: apiItem.barcode || apiItem.Barcode || '',
          unit: apiItem.unit || apiItem.Unit || 'adet',
          salePrice: apiItem.salePrice || apiItem.SalePrice || 0,
          canBeNegative: apiItem.canBeNegative ?? apiItem.CanBeNegative ?? true,
          isActive: apiItem.isActive ?? apiItem.IsActive ?? true,
          hasRecipe: apiItem.hasRecipe ?? apiItem.HasRecipe ?? false,
          hasMenu: apiItem.hasMenu ?? apiItem.HasMenu ?? false,
        }))
        .filter((i: InventoryItem) => 
          i.id !== item?.id && // Kendisi değil
          !existingIds.includes(i.id) && // Zaten eklenmemiş
          i.isActive !== false && // Aktif olan
          !i.hasMenu // ⚠️ MENÜ DEĞİL - Menü içine menü eklenemez!
        );
      
      setSearchOptions(mappedItems);
      console.log(`✅ [MENÜ ARAMA] "${searchTerm}" için ${mappedItems.length} ürün bulundu`);
    } catch (err) {
      console.error('❌ [MENÜ ARAMA] Hata:', err);
      setSearchOptions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // ✅ Debounced arama - kullanıcı yazarken 300ms bekle
  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    
    // Önceki timeout'u temizle
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Yeni timeout başlat
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(value);
    }, 300);
  };

  // Modal açıldığında
  useEffect(() => {
    if (open && item) {
      loadMenu();
      setSearchOptions([]); // Arama sonuçlarını temizle
      setSearchInputValue('');
      setPendingItems([]);
      setMenuName(`${item.name} ${tt('Menüsü', 'Menu')}`);
      setMenuCode('');
      setDescription('');
      setError(null);
      setSuccessMessage(null);
      setMenuConflictError(null);
      setShowInfoBox(false);
      // Taslaklar tarayıcıda; başka sekmede eklenmiş olabilir diye her açılışta okunur
      setTemplates(loadMenuTemplates(companyId));
      setApplyReport(null);
    }
  }, [open, item]);

  // Menü yükle — yüklenen menüyü döner (taslak uygulaması taze satır id'lerine ihtiyaç duyar)
  const loadMenu = async (): Promise<ProductMenu | null> => {
    if (!item || !companyId) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      // ✅ Önce ana ürünün güncel bilgisini çek
      let freshItem = null;
      try {
        const itemResponse = await accountingApi.get(
          `/api/Inventory/items/${companyId}/${item.id}`
        );
        freshItem = itemResponse.data;
      } catch {}
      const currentCanBeNegative = freshItem?.canBeNegative ?? item.canBeNegative;

      let response;
      try {
        response = await accountingApi.get(
          `/api/ProductMenu/by-item/${item.id}`
        );
      } catch (err: any) {
        if (err.response?.status === 404) {
          setMenu(null);
          return null;
        }
        throw new Error(tt('Menü yüklenirken hata oluştu', 'An error occurred while loading menu'));
      }
      
      // NOT: Taslak uygulanırken ekstra opsiyonlar yeni oluşan menü satırının
      // id'sine bağlanacağı için çağıran taraf taze veriye ihtiyaç duyar.
      let data: ProductMenu = response.data;
      
      // ✅ Menüdeki allowNegativeStock ile ana ürünün güncel canBeNegative değeri farklıysa güncelle
      if (data.allowNegativeStock !== currentCanBeNegative) {
        console.log(`🔄 [MENÜ] Ana ürün canBeNegative değişmiş, menü güncelleniyor: ${data.allowNegativeStock} → ${currentCanBeNegative}`);
        
        try {
          await accountingApi.post(
            `/api/ProductMenu/${data.id}/update`,
            {
              ...data,
              allowNegativeStock: currentCanBeNegative
            }
          );
          data = { ...data, allowNegativeStock: currentCanBeNegative };
        } catch {}
      }
      
      setMenu(data);
      setMenuName(data.menuName);
      setMenuCode(data.menuCode || '');
      setDescription(data.description || '');
      return data;
    } catch (err: any) {
      console.error('Menü yükleme hatası:', err);
      setMenu(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Menü oluştur
  const createMenu = async () => {
    if (!item || !companyId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await accountingApi.post(`/api/ProductMenu`, {
        companyId,
        inventoryItemId: item.id,
        menuName: menuName || `${item.name} ${tt('Menüsü', 'Menu')}`,
        menuCode: menuCode || undefined,
        description: description || undefined,
        allowNegativeStock: item.canBeNegative
      });
      
      const data: ProductMenu = response.data;
      setMenu(data);
      setSuccessMessage(tt('Menü başarıyla oluşturuldu!', 'Menu created successfully!'));
      onSuccess?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Menü çakışma kontrolü (menü içine menü eklenemez)
  const checkMenuConflict = async (itemId: string): Promise<boolean> => {
    if (!companyId) return false;
    
    try {
      const response = await accountingApi.post(`/api/ProductMenu/check-conflict`, {
        companyId,
        itemId
      });
      
      const data = response.data;
      if (data.isMenuItem) {
        setMenuConflictError(data.message || tt('Bu ürün bir menüdür. Menü içine başka menü eklenemez!', 'This product is a menu. You cannot add another menu into a menu!'));
        return true;
      }
      
      setMenuConflictError(null);
      return false;
    } catch {
      return false;
    }
  };

  // ✅ Ürün seçim modalından seçilenleri bekleyen listeye ekle (+ menü çakışma kontrolü)
  const handleProductsSelected = async (products: InventoryItem[]) => {
    setMenuConflictError(null);
    const existing = new Set(pendingItems.map(p => p.item.id));
    const newRows: PendingMenuItem[] = products
      .filter(p => !existing.has(p.id))
      .map(p => ({ item: p, quantity: '', wastagePercent: '0', notes: '' }));
    if (newRows.length === 0) return;

    setPendingItems(prev => [...prev, ...newRows]);

    // Menü çakışma kontrolü (menü içine menü eklenemez — erken uyarı)
    for (const row of newRows) {
      const isConflict = await checkMenuConflict(row.item.id);
      if (isConflict) {
        setPendingItems(prev => prev.filter(p => p.item.id !== row.item.id));
      }
    }
  };

  // ✅ API'den güncel ürün bilgisini çek
  const fetchItemFromAPI = async (itemId: string): Promise<InventoryItem | null> => {
    try {
      const response = await accountingApi.get(
        `/api/Inventory/items/${companyId}/${itemId}`
      );
      return response.data;
    } catch (error) {
      console.error('Ürün bilgisi çekilemedi:', error);
      return null;
    }
  };

  // ✅ Bekleyen ürünleri toplu ekle (her satır için miktar zorunlu)
  const handleAddPendingItems = async () => {
    if (!menu || pendingItems.length === 0) return;

    const missingQuantity = pendingItems.find(p => parseDecimalTR(p.quantity) <= 0);
    if (missingQuantity) {
      setError(tt(
        `'${missingQuantity.item.name}' için geçerli bir miktar girin.`,
        `Enter a valid quantity for '${missingQuantity.item.name}'.`
      ));
      return;
    }

    setAddingItem(true);
    setError(null);

    const failed: string[] = [];
    let sortOrder = (menu.menuItems?.length || 0) + 1;

    for (const row of pendingItems) {
      let currentSelectedItem = { ...row.item };

      // ✅ API'den güncel canBeNegative değerini çek
      const freshItem = await fetchItemFromAPI(currentSelectedItem.id);
      if (freshItem) {
        currentSelectedItem = { ...currentSelectedItem, canBeNegative: freshItem.canBeNegative };
      }

      // CanBeNegative tutarlılık kontrolü (güncel veri ile)
      if (menu.allowNegativeStock !== currentSelectedItem.canBeNegative) {
        failed.push(
          menu.allowNegativeStock
            ? tt(`'${currentSelectedItem.name}' ürünü için 'Stok eksiye düşebilir' ayarı kapalı. Menü ayarı ile uyuşmuyor!`, `'Can go negative' is disabled for '${currentSelectedItem.name}'. It does not match the menu setting!`)
            : tt(`'${currentSelectedItem.name}' ürünü için 'Stok eksiye düşebilir' ayarı açık. Menü ayarı ile uyuşmuyor!`, `'Can go negative' is enabled for '${currentSelectedItem.name}'. It does not match the menu setting!`)
        );
        continue;
      }

      try {
        await accountingApi.post(
          `/api/ProductMenu/${menu.id}/items`,
          {
            companyId,
            menuId: menu.id,
            itemId: currentSelectedItem.id,
            quantity: parseDecimalTR(row.quantity),
            unit: currentSelectedItem.unit,
            wastagePercent: parseDecimalTR(row.wastagePercent),
            notes: row.notes || undefined,
            sortOrder: sortOrder++
          }
        );
        // Başarılı eklenen satırı listeden düş
        setPendingItems(prev => prev.filter(p => p.item.id !== row.item.id));
      } catch (err: any) {
        failed.push(`'${currentSelectedItem.name}': ${err.response?.data?.message || err.message}`);
      }
    }

    // Menüyü yeniden yükle
    await loadMenu();

    if (failed.length > 0) {
      setError(failed.join(' • '));
    } else {
      setSuccessMessage(tt('Ürünler eklendi!', 'Products added!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    setAddingItem(false);
  };

  // Ürün güncelle
  const handleUpdateItem = async () => {
    if (!editingMenuItem || !companyId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductMenu/items/${editingMenuItem.id}/update`,
        {
          quantity: editingMenuItem.quantity,
          wastagePercent: editingMenuItem.wastagePercent,
          notes: editingMenuItem.notes
        }
      );
      
      await loadMenu();
      setEditingMenuItem(null);
      setSuccessMessage(tt('Ürün güncellendi!', 'Product updated!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Ürün silme dialog'unu aç
  const openDeleteItemDialog = (menuItemId: string) => {
    setMenuItemToDelete(menuItemId);
    setDeleteItemDialogOpen(true);
  };

  // Ürün sil (dialog onayından sonra)
  const handleDeleteItem = async () => {
    if (!companyId || !menuItemToDelete) return;
    
    setDeleteItemDialogOpen(false);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductMenu/items/${menuItemToDelete}/delete`
      );
      
      await loadMenu();
      setSuccessMessage(tt('Ürün silindi!', 'Product deleted!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setMenuItemToDelete(null);
    }
  };

  // Menü silme dialog'unu aç
  const openDeleteMenuDialog = () => {
    setDeleteMenuDialogOpen(true);
  };

  // Menü sil (dialog onayından sonra)
  const handleDeleteMenu = async () => {
    if (!menu || !companyId) return;
    
    setDeleteMenuDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductMenu/${menu.id}/delete`
      );
      
      setMenu(null);
      setSuccessMessage(tt('Menü silindi!', 'Menu deleted!'));
      onSuccess?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // 3 NOKTA MENÜSÜ HANDLER'LARI
  // ═══════════════════════════════════════════════════════════════

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, menuItem: ProductMenuItem) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMenuItemForAction(menuItem);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMenuItemForAction(null);
  };

  const handleEditFromMenu = () => {
    if (selectedMenuItemForAction) {
      setEditingMenuItem({
        id: selectedMenuItemForAction.id,
        quantity: selectedMenuItemForAction.quantity,
        wastagePercent: selectedMenuItemForAction.wastagePercent,
        notes: selectedMenuItemForAction.notes
      });
    }
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedMenuItemForAction) {
      openDeleteItemDialog(selectedMenuItemForAction.id);
    }
    handleMenuClose();
  };

  const handleAddExtraOptionFromMenu = async () => {
    if (selectedMenuItemForAction) {
      setShowExtraOptionsModal(true);
      setExtraOptionItems([]);
      // NOT: handleMenuClose() çağırmıyoruz çünkü selectedMenuItemForAction'ı korumamız gerekiyor
      setMenuAnchorEl(null); // Sadece menüyü kapat, selectedMenuItemForAction'ı koru
      
      // ✅ Mevcut ekstra opsiyonları yükle (tekrar eklenmesin)
      if (selectedMenuItemForAction.extraOptions && selectedMenuItemForAction.extraOptions.length > 0) {
        setExistingExtraOptions(selectedMenuItemForAction.extraOptions);
      } else {
        setExistingExtraOptions([]);
      }
    } else {
      handleMenuClose();
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // OPSİYONLU ÜRÜN EKLEME
  // ═══════════════════════════════════════════════════════════════

  /** Gelişmiş seçim modalından gelenleri opsiyon grubuna ekler (tekrarları atlar). */
  const handleOptionalProductsSelected = (products: InventoryItem[]) => {
    setSelectedOptionalItems(prev => {
      const existing = new Set(prev.map(p => p.id));
      return [...prev, ...products.filter(p => !existing.has(p.id))];
    });
  };

  const handleAddOptionalItems = async () => {
    if (!menu || !companyId || selectedOptionalItems.length === 0 || !optionGroupName.trim()) {
      setError(tt('Opsiyon grubu adı ve en az bir ürün seçilmelidir', 'Option group name and at least one product must be selected'));
      return;
    }

    setAddingOptionalItems(true);
    setError(null);

    try {
      const requestBody = {
        companyId,
        menuId: menu.id,
        optionGroupName: optionGroupName.trim(),
        items: selectedOptionalItems.map((item, index) => ({
          itemId: item.id,
          quantity: 1,
          unit: item.unit,
          wastagePercent: 0,
          isDefaultOption: index === defaultOptionIndex,
          notes: null
        }))
      };

      await accountingApi.post(
        `/api/ProductMenu/${menu.id}/optional-items`,
        requestBody
      );

      await loadMenu();
      setShowOptionalItemsModal(false);
      setOptionGroupName('');
      setSelectedOptionalItems([]);
      setDefaultOptionIndex(0);
      setSuccessMessage(tt('Opsiyonlu ürünler eklendi!', 'Optional products added!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingOptionalItems(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // EKSTRA OPSİYON EKLEME
  // ═══════════════════════════════════════════════════════════════

  const handleAddExtraOptions = async () => {
    console.log('[EXTRA OPTIONS] handleAddExtraOptions çağrıldı:', {
      selectedMenuItemForAction,
      companyId,
      extraOptionItemsLength: extraOptionItems.length,
      extraOptionItems
    });
    
    if (!selectedMenuItemForAction) {
      setError(tt('Lütfen bir menü öğesi seçin', 'Please select a menu item'));
      return;
    }
    if (!companyId) {
      setError(tt('Şirket bilgisi bulunamadı', 'Company information not found'));
      return;
    }
    if (extraOptionItems.length === 0) {
      setError(tt('En az bir ekstra opsiyon seçilmelidir', 'At least one extra option must be selected'));
      return;
    }

    setAddingExtraOptions(true);
    setError(null);

    try {
      const requestBody = {
        companyId,
        menuItemId: selectedMenuItemForAction.id,
        options: extraOptionItems.map(opt => ({
          optionItemId: opt.item.id,
          optionItemName: opt.item.name,
          optionItemUnit: opt.item.unit,
          priceDifference: parseDecimalTR(opt.priceDifference)
        }))
      };

      await accountingApi.post(
        `/api/ProductMenu/items/${selectedMenuItemForAction.id}/extra-options`,
        requestBody
      );

      await loadMenu();
      setShowExtraOptionsModal(false);
      setExtraOptionItems([]);
      setExistingExtraOptions([]);
      setSelectedMenuItemForAction(null);
      setSuccessMessage(tt('Ekstra opsiyonlar eklendi!', 'Extra options added!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setAddingExtraOptions(false);
    }
  };

  // ✅ Mevcut ekstra opsiyon silme
  const handleDeleteExtraOption = async (extraOptionId: string) => {
    if (!companyId) return;
    
    setDeletingExtraOptionId(extraOptionId);
    try {
      await accountingApi.post(
        `/api/ProductMenu/extra-options/${extraOptionId}/delete`
      );
      
      // Listeden kaldır
      setExistingExtraOptions(prev => prev.filter(eo => eo.id !== extraOptionId));
      setSuccessMessage(tt('Ekstra opsiyon silindi!', 'Extra option deleted!'));
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Ana menüyü de yenile
      await loadMenu();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingExtraOptionId(null);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // TASLAK KAYDET / UYGULA
  // ═══════════════════════════════════════════════════════════════

  const openSaveTemplateModal = () => {
    setTemplateNameInput(menu?.menuName || item?.name || '');
    setShowSaveTemplateModal(true);
  };

  /**
   * Menünün mevcut içeriğini taslağa çevirip tarayıcıya yazar.
   * Opsiyonlu satırlar optionGroupId'ye göre gruplanır; id yoksa grup adına
   * düşülür (eski kayıtlarda optionGroupId boş olabiliyor).
   */
  const handleSaveTemplate = () => {
    if (!menu || !companyId) return;

    const name = templateNameInput.trim();
    if (!name) {
      setError(tt('Taslak adı giriniz', 'Enter a template name'));
      return;
    }

    const allRows = menu.menuItems || [];
    if (allRows.length === 0) {
      setError(tt('Menüde kaydedilecek ürün yok', 'There are no products in the menu to save'));
      return;
    }

    const toExtraOptions = (row: ProductMenuItem) =>
      (row.extraOptions || []).map(eo => ({
        optionItemId: eo.optionItemId,
        optionItemName: eo.optionItemName || '',
        optionItemUnit: eo.optionItemUnit || '',
        priceDifference: eo.priceDifference,
      }));

    const items = allRows
      .filter(row => !row.isOptional)
      .map(row => ({
        itemId: row.itemId,
        itemName: row.itemName || '',
        unit: row.unit,
        quantity: row.quantity,
        wastagePercent: row.wastagePercent,
        notes: row.notes,
        extraOptions: toExtraOptions(row),
      }));

    const groupMap = new Map<string, { optionGroupName: string; items: any[] }>();
    for (const row of allRows.filter(r => r.isOptional)) {
      const key = row.optionGroupId || row.optionGroupName || 'default';
      const group = groupMap.get(key) || {
        optionGroupName: row.optionGroupName || tt('Opsiyon Grubu', 'Option Group'),
        items: [],
      };
      group.items.push({
        itemId: row.itemId,
        itemName: row.itemName || '',
        unit: row.unit,
        quantity: row.quantity,
        wastagePercent: row.wastagePercent,
        isDefaultOption: row.isDefaultOption === true,
        extraOptions: toExtraOptions(row),
      });
      groupMap.set(key, group);
    }

    const { templates: next, saved } = saveMenuTemplate(companyId, {
      name,
      sourceItemName: item?.name,
      items,
      optionGroups: Array.from(groupMap.values()),
    });

    if (!saved) {
      setError(tt(
        'Taslak kaydedilemedi. Tarayıcı depolama alanı dolu olabilir.',
        'Template could not be saved. Browser storage may be full.'
      ));
      return;
    }

    setTemplates(next);
    setShowSaveTemplateModal(false);
    setError(null);
    setSuccessMessage(tt(`"${name}" taslağı kaydedildi.`, `Template "${name}" saved.`));
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (!companyId) return;
    setTemplates(deleteMenuTemplate(companyId, templateId));
  };

  /**
   * Taslağı açık menüye uygular: normal ürünler tek tek, opsiyon grupları toplu
   * eklenir; ekstra opsiyonlar menü satırları oluştuktan sonra itemId eşleşmesi
   * ile bağlanır (satır id'si ancak ekleme sonrası biliniyor).
   *
   * Menüde zaten olan ürünler atlanır — backend "zaten eklenmiş" hatası dönerdi
   * ve kullanıcı hatalar arasında gerçek sorunu göremezdi. Atlananlar ve API
   * hataları özet olarak bildirilir; ilk hatada durmaz, kalanı eklemeye devam eder.
   */
  const handleApplyTemplate = async (template: MenuTemplate) => {
    if (!menu || !companyId) return;

    setApplyingTemplateId(template.id);
    setError(null);
    setApplyReport(null);

    const problems: string[] = [];
    const skipped: string[] = [];
    let addedCount = 0;
    let sortOrder = (menu.menuItems?.length || 0) + 1;
    const existingItemIds = new Set((menu.menuItems || []).map(mi => mi.itemId));

    try {
      // 1) Normal ürünler
      for (const row of template.items) {
        if (existingItemIds.has(row.itemId)) {
          skipped.push(row.itemName);
          continue;
        }
        try {
          await accountingApi.post(`/api/ProductMenu/${menu.id}/items`, {
            companyId,
            menuId: menu.id,
            itemId: row.itemId,
            quantity: row.quantity,
            unit: row.unit,
            wastagePercent: row.wastagePercent,
            notes: row.notes || undefined,
            sortOrder: sortOrder++,
          });
          addedCount++;
        } catch (err: any) {
          problems.push(`'${row.itemName}': ${err.response?.data?.message || err.message}`);
        }
      }

      // 2) Opsiyon grupları
      for (const group of template.optionGroups) {
        const groupItems = group.items.filter(gi => !existingItemIds.has(gi.itemId));
        if (groupItems.length === 0) {
          skipped.push(group.optionGroupName);
          continue;
        }
        try {
          await accountingApi.post(`/api/ProductMenu/${menu.id}/optional-items`, {
            companyId,
            menuId: menu.id,
            optionGroupName: group.optionGroupName,
            items: groupItems.map(gi => ({
              itemId: gi.itemId,
              quantity: gi.quantity,
              unit: gi.unit,
              wastagePercent: gi.wastagePercent,
              // Varsayılanı olmayan grup kalmasın: taslakta işaret yoksa ilk ürün
              isDefaultOption: groupItems.some(x => x.isDefaultOption)
                ? gi.isDefaultOption
                : gi === groupItems[0],
              notes: null,
            })),
          });
          addedCount += groupItems.length;
        } catch (err: any) {
          problems.push(`'${group.optionGroupName}': ${err.response?.data?.message || err.message}`);
        }
      }

      // 3) Ekstra opsiyonlar — satırlar oluştuktan sonra bağlanır
      const extrasByItemId = new Map<string, typeof template.items[number]['extraOptions']>();
      for (const row of template.items) {
        if (row.extraOptions?.length) extrasByItemId.set(row.itemId, row.extraOptions);
      }
      for (const group of template.optionGroups) {
        for (const gi of group.items) {
          if (gi.extraOptions?.length) extrasByItemId.set(gi.itemId, gi.extraOptions);
        }
      }

      const refreshed = await loadMenu();

      if (extrasByItemId.size > 0 && refreshed) {
        let extrasAdded = false;
        for (const [itemId, extras] of extrasByItemId) {
          const createdRow = refreshed.menuItems?.find(mi => mi.itemId === itemId);
          if (!createdRow || !extras) continue;

          const alreadyThere = new Set((createdRow.extraOptions || []).map(eo => eo.optionItemId));
          const newExtras = extras.filter(eo => !alreadyThere.has(eo.optionItemId));
          if (newExtras.length === 0) continue;

          try {
            await accountingApi.post(`/api/ProductMenu/items/${createdRow.id}/extra-options`, {
              companyId,
              menuItemId: createdRow.id,
              options: newExtras,
            });
            extrasAdded = true;
          } catch (err: any) {
            problems.push(`'${createdRow.itemName}' ${tt('ekstra opsiyonları', 'extra options')}: ${err.response?.data?.message || err.message}`);
          }
        }
        if (extrasAdded) await loadMenu();
      }

      const parts: string[] = [];
      parts.push(tt(`${addedCount} ürün eklendi.`, `${addedCount} products added.`));
      if (skipped.length > 0) {
        parts.push(tt(
          `Menüde zaten olduğu için atlandı: ${skipped.join(', ')}.`,
          `Skipped (already in the menu): ${skipped.join(', ')}.`
        ));
      }
      setApplyReport(parts.join(' '));

      if (problems.length > 0) {
        setError(problems.join(' • '));
      } else {
        setShowApplyTemplateModal(false);
        setSuccessMessage(parts.join(' '));
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } finally {
      setApplyingTemplateId(null);
    }
  };

  // Fire dahil toplam miktar hesapla
  const calculateTotalWithWastage = (quantity: number, wastagePercent: number): number => {
    return quantity * (1 + wastagePercent / 100);
  };

  // ✅ Miktar formatlama - gereksiz sıfırları kaldır, Türkçe format
  const formatQuantity = (value: number): string => {
    // Tam sayıysa ondalık gösterme
    if (Number.isInteger(value)) {
      return value.toString();
    }
    // Ondalıklı ise gereksiz sıfırları kaldır (max 3 basamak)
    const formatted = value.toFixed(3);
    return formatted.replace(/\.?0+$/, '').replace('.', ',');
  };

  // ✅ Yazdırma fonksiyonu
  const handlePrint = () => {
    if (!menu || !menu.menuItems?.length) return;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title></title>
        <style>
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          @media print {
            html, body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #333;
          }
          .header h1 {
            font-size: 18pt;
            font-weight: bold;
            text-transform: uppercase;
          }
          .product-info {
            margin-bottom: 15px;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 4px;
          }
          .product-info p {
            margin: 3px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
            font-size: 10pt;
          }
          th {
            background: #333;
            color: #fff;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background: #f9f9f9;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .has-recipe {
            color: #ed6c02;
            font-weight: bold;
          }
          .summary {
            margin-top: 15px;
            padding: 10px;
            background: #e3f2fd;
            border: 1px solid #1976d2;
            border-radius: 4px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${(item?.name || menu.menuName || '').toUpperCase()} ${tt('MENÜSÜ', 'MENU')}</h1>
        </div>
        
        <div class="product-info">
          <p><strong>${tt('Ürün', 'Product')}:</strong> ${item?.name || '-'}</p>
          <p><strong>${tt('Stok Kodu', 'Stock Code')}:</strong> ${item?.stockCode || '-'}</p>
          ${menu.description ? `<p><strong>${tt('Açıklama', 'Description')}:</strong> ${menu.description}</p>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 5%">#</th>
              <th style="width: 40%">${tt('Ürün', 'Product')}</th>
              <th style="width: 15%">${tt('Birim', 'Unit')}</th>
              <th class="text-right" style="width: 15%">${tt('Miktar', 'Quantity')}</th>
              <th class="text-right" style="width: 15%">${tt('Fire %', 'Waste %')}</th>
              <th class="text-center" style="width: 10%">${tt('Reçete', 'Recipe')}</th>
            </tr>
          </thead>
          <tbody>
            ${menu.menuItems.map((mi, idx) => `
              <tr>
                <td class="text-center">${idx + 1}</td>
                <td>${mi.itemName || '-'}</td>
                <td>${mi.unit || '-'}</td>
                <td class="text-right">${formatQuantity(mi.quantity)}</td>
                <td class="text-right">${formatQuantity(mi.wastagePercent)}%</td>
                <td class="text-center ${mi.hasRecipe ? 'has-recipe' : ''}">${mi.hasRecipe ? tt('Evet', 'Yes') : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="summary">
          ${tt('Toplam', 'Total')} ${menu.menuItems.length} ${tt('ürün', 'products')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={menu ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: { minHeight: menu ? '50vh' : 'auto' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuIcon color="info" />
          <Typography variant="h6">
            {tt('Menü Tanımları', 'Menu Definitions')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={tt('Menü yönetimi bilgisi', 'Menu management information')}>
            <IconButton
              size="small"
              onClick={() => setShowInfoBox(prev => !prev)}
              sx={{
                color: showInfoBox ? 'info.main' : 'text.secondary',
                border: '1px solid',
                borderColor: showInfoBox ? 'info.main' : 'divider',
                borderRadius: 1
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        {showInfoBox && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {tt('Menü Tanımları Hakkında', 'About Menu Definitions')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {tt(
                'Bu modal, birlikte satılan ürünleri tek bir menü altında toplamanızı ve satışta standart içerik yönetimi yapmanızı sağlar.',
                'This modal lets you group products sold together under one menu and manage a consistent sale content.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Önce menü adı/kodu oluşturun, sonra "Ürün Ekle" ile menüye dahil olacak ürünleri miktar ve fire oranı ile tanımlayın.',
                '• First create the menu name/code, then use "Add Product" to define included products with quantity and waste rate.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• "Opsiyonlu Ürün Ekle" ile kullanıcıya tek seçimli alternatifler sunabilir, varsayılan seçeneği belirleyebilirsiniz.',
                '• With "Add Optional Product", you can offer single-choice alternatives and set a default option.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Ürün satırındaki 3 nokta menüsünden düzenleme, silme ve ekstra opsiyon ekleme işlemlerini yönetin.',
                '• Use the three-dot row menu to edit, delete, and add extra options for each item.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Menü içinde başka menü tanımlanamaz; yalnızca normal ürünler eklenir. Bu kural satış akışını güvenli tutar.',
                '• You cannot add a menu inside another menu; only normal products are allowed. This keeps the sales flow safe.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• İçeriği aynı olan çok sayıda menü varsa birini "Taslak Olarak Kaydet" ile saklayın, diğerlerinde "Taslaktan Uygula" ile tek tıkla ekleyin. Taslak bu bilgisayarın tarayıcısında tutulur.',
                '• If many menus share the same content, store one with "Save as Template" and add it to the others with a single click via "Apply Template". Templates are kept in this computer\'s browser.'
              )}
            </Typography>
          </Alert>
        )}

        {/* Ürün Bilgisi - Kompakt */}
        {item && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {tt('Menü Ürünü', 'Menu Product')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                  {item.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={item.stockCode} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                {item.barcode && <Chip label={item.barcode} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                <Chip 
                  label={item.canBeNegative ? tt('Eksiye Düşebilir', 'Can Go Negative') : tt('Eksiye Düşemez', 'Cannot Go Negative')}
                  size="small" 
                  color={item.canBeNegative ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Hata ve Başarı Mesajları */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {menuConflictError && <Alert severity="warning" sx={{ mb: 2 }}>{menuConflictError}</Alert>}
        
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Menü Yok - Oluştur */}
        {!loading && !menu && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <MenuIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {tt('Henüz menü tanımlanmamış', 'No menu defined yet')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {tt('Satışta birlikte satılacak ürünleri belirleyin', 'Select products to be sold together')}
            </Typography>
            
            <Box sx={{ maxWidth: 350, mx: 'auto' }}>
              <TextField
                fullWidth
                size="small"
                label={tt('Menü Adı', 'Menu Name')}
                value={menuName}
                onChange={(e) => setMenuName(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={tt('Menü Kodu (Opsiyonel)', 'Menu Code (Optional)')}
                value={menuCode}
                onChange={(e) => setMenuCode(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                color="info"
                startIcon={<AddIcon />}
                onClick={createMenu}
                disabled={saving}
              >
                {saving ? tt('Oluşturuluyor...', 'Creating...') : tt('Menü Oluştur', 'Create Menu')}
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Menü Var - Ürün Listesi */}
        {!loading && menu && (
          <>
            {/* Menü Bilgileri */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {tt('Menü', 'Menu')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="info.main">
                {menu.menuName}
              </Typography>
              {menu.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {menu.description}
                </Typography>
              )}
            </Box>

            {/* Ürün Ekle Butonları */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setIsProductSelectOpen(true)}
              >
                {tt('Ürün Ekle', 'Add Product')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<OptionIcon />}
                onClick={() => setShowOptionalItemsModal(true)}
              >
                {tt('Opsiyonlu Ürün Ekle', 'Add Optional Product')}
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              {/* Taslaklar: aynı içerikli menüleri tek tek doldurmamak için */}
              <Tooltip
                title={tt(
                  'Bu menünün içeriğini taslak olarak bilgisayarınıza kaydedin',
                  'Save this menu content as a template on your computer'
                )}
              >
                <span>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<BookmarkAddIcon />}
                    onClick={openSaveTemplateModal}
                    disabled={(menu.menuItems?.length || 0) === 0}
                  >
                    {tt('Taslak Olarak Kaydet', 'Save as Template')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  templates.length === 0
                    ? tt('Kayıtlı taslak yok', 'No saved templates')
                    : tt('Kayıtlı bir taslağı bu menüye uygula', 'Apply a saved template to this menu')
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<BookmarksIcon />}
                    onClick={() => {
                      setApplyReport(null);
                      setShowApplyTemplateModal(true);
                    }}
                    disabled={templates.length === 0}
                  >
                    {tt('Taslaktan Uygula', 'Apply Template')}
                    {templates.length > 0 ? ` (${templates.length})` : ''}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Seçilen ürünler — miktar/fire/not girilip toplu eklenir */}
            {pendingItems.length > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {tt('Seçilen Ürünler', 'Selected Products')} ({pendingItems.length})
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tt('⚠️ Menü içine başka menü eklenemez! Sadece normal ürünler eklenebilir.', '⚠️ You cannot add another menu into a menu! Only regular products can be added.')}
                </Alert>
                {pendingItems.map((row, index) => (
                  <Box key={row.item.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 220, flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {row.item.name}
                        {row.item.hasRecipe && (
                          <Chip 
                            label={tt('Reçeteli', 'Has Recipe')}
                            size="small" 
                            color="warning" 
                            sx={{ ml: 1, height: 20 }}
                            icon={<RecipeIcon sx={{ fontSize: 14 }} />}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.item.stockCode} | {tt('Birim', 'Unit')}: {row.item.unit} | {row.item.canBeNegative ? tt('✅ Eksiye düşebilir', '✅ Can go negative') : tt('❌ Eksiye düşemez', '❌ Cannot go negative')}
                      </Typography>
                    </Box>

                    <TextField
                      label={tt('Miktar', 'Quantity')}
                      type="number"
                      size="small"
                      value={row.quantity}
                      onChange={(e) => {
                        const next = [...pendingItems];
                        next[index] = { ...row, quantity: e.target.value };
                        setPendingItems(next);
                      }}
                      sx={{ width: 120 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{row.item.unit}</InputAdornment>
                        )
                      }}
                    />

                    <TextField
                      label={tt('Fire %', 'Waste %')}
                      type="number"
                      size="small"
                      value={row.wastagePercent}
                      onChange={(e) => {
                        const next = [...pendingItems];
                        next[index] = { ...row, wastagePercent: e.target.value };
                        setPendingItems(next);
                      }}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />

                    <TextField
                      label={tt('Not (Opsiyonel)', 'Note (Optional)')}
                      size="small"
                      value={row.notes}
                      onChange={(e) => {
                        const next = [...pendingItems];
                        next[index] = { ...row, notes: e.target.value };
                        setPendingItems(next);
                      }}
                      sx={{ width: 150 }}
                    />

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setPendingItems(prev => prev.filter(p => p.item.id !== row.item.id))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={addingItem ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleAddPendingItems}
                    disabled={addingItem || pendingItems.length === 0}
                  >
                    {tt(`Hepsini Ekle (${pendingItems.length})`, `Add All (${pendingItems.length})`)}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setPendingItems([]);
                      setMenuConflictError(null);
                    }}
                  >
                    {tt('İptal', 'Cancel')}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Ürün Listesi */}
            {menu.menuItems && menu.menuItems.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>{tt('Ürün', 'Product')}</TableCell>
                      <TableCell align="center">{tt('Tip', 'Type')}</TableCell>
                      <TableCell align="center">{tt('Miktar', 'Quantity')}</TableCell>
                      <TableCell align="center">{tt('Birim', 'Unit')}</TableCell>
                      <TableCell align="center">{tt('Fire %', 'Waste %')}</TableCell>
                      <TableCell align="center">{tt('Reçete', 'Recipe')}</TableCell>
                      <TableCell align="center">{tt('Ekstra', 'Extra')}</TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>{tt('İşlemler', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {menu.menuItems.map((menuItem) => (
                      <TableRow 
                        key={menuItem.id}
                        sx={{ 
                          bgcolor: menuItem.isOptional ? 'secondary.50' : 'inherit',
                          borderLeft: menuItem.isOptional ? '3px solid' : 'none',
                          borderLeftColor: menuItem.isOptional ? 'secondary.main' : 'transparent'
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {menuItem.itemName}
                              </Typography>
                              {menuItem.isDefaultOption && (
                                <Chip label={tt('Varsayılan', 'Default')} size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {menuItem.itemStockCode}
                              {menuItem.optionGroupName && ` • ${menuItem.optionGroupName}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        {/* Tip Sütunu */}
                        <TableCell align="center">
                          {menuItem.isOptional ? (
                            <Chip 
                              label={tt('Opsiyon', 'Option')}
                              size="small" 
                              color="secondary" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ) : (
                            <Chip 
                              label={tt('Normal', 'Normal')}
                              size="small" 
                              color="default" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {editingMenuItem?.id === menuItem.id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editingMenuItem.quantity}
                              onChange={(e) => setEditingMenuItem({
                                ...editingMenuItem,
                                quantity: parseDecimalTR(e.target.value)
                              })}
                              sx={{ width: 80 }}
                            />
                          ) : (
                            <Typography variant="body2">{formatQuantity(menuItem.quantity)}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{menuItem.unit}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          {editingMenuItem?.id === menuItem.id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editingMenuItem.wastagePercent}
                              onChange={(e) => setEditingMenuItem({
                                ...editingMenuItem,
                                wastagePercent: parseDecimalTR(e.target.value)
                              })}
                              sx={{ width: 60 }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {menuItem.wastagePercent > 0 ? `${formatQuantity(menuItem.wastagePercent)}%` : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {menuItem.hasRecipe ? (
                            <Tooltip title={tt('Bu ürünün reçetesi var - Satışta malzemeler de düşer', 'This product has a recipe - ingredients are also deducted in sale')}>
                              <RecipeIcon fontSize="small" color="warning" />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        {/* Ekstra Opsiyon Sütunu */}
                        <TableCell align="center">
                          {menuItem.extraOptions && menuItem.extraOptions.length > 0 ? (
                            <Tooltip title={`${menuItem.extraOptions.length} ${tt('ekstra opsiyon', 'extra options')}: ${menuItem.extraOptions.map(eo => `${eo.optionItemName} (+${eo.priceDifference.toLocaleString(localeCode)}₺)`).join(', ')}`}>
                              <Chip 
                                label={menuItem.extraOptions.length} 
                                size="small" 
                                color="info" 
                                sx={{ fontSize: '0.7rem', minWidth: 24 }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        {/* İşlemler Sütunu - 3 Nokta Menüsü */}
                        <TableCell align="center">
                          {editingMenuItem?.id === menuItem.id ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleUpdateItem}
                                disabled={saving}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setEditingMenuItem(null)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, menuItem)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                {tt('Henüz ürün eklenmemiş. "Ürün Ekle" veya "Opsiyonlu Ürün Ekle" butonunu kullanarak ürün ekleyin.', 'No products added yet. Use the "Add Product" or "Add Optional Product" button to add products.')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      {/* 3 Nokta Menüsü */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditFromMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{tt('Düzenle', 'Edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{tt('Sil', 'Delete')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleAddExtraOptionFromMenu}>
          <ListItemIcon>
            <TuneIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText>{tt('Ekstra Opsiyon Ekle', 'Add Extra Option')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Opsiyonlu Ürün Ekleme Modalı */}
      <Dialog
        open={showOptionalItemsModal}
        onClose={() => setShowOptionalItemsModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <OptionIcon color="secondary" />
          {tt('Opsiyonlu Ürün Ekle', 'Add Optional Product')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt('Aynı opsiyon grubundan sadece ', 'Only ')}<strong>{tt('BİR', 'ONE')}</strong>{tt(' ürün seçilebilir.', ' product can be selected from the same option group.')}
          </Alert>

          <TextField
            label={tt('Opsiyon Grubu Adı', 'Option Group Name')}
            placeholder={tt('Örn: İçecek, Sos', 'Ex: Drink, Sauce')}
            fullWidth
            value={optionGroupName}
            onChange={(e) => setOptionGroupName(e.target.value)}
            sx={{ mb: 2 }}
            size="small"
            required
          />

          {/* ✅ Ürün seçimi: "Ürün Ekle" ile aynı gelişmiş modal (arama + depo/kategori filtresi + sayfalama) */}
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setIsOptionalProductSelectOpen(true)}
            sx={{ mb: 2 }}
          >
            {tt('Ürün Seç', 'Select Products')}
          </Button>

          {/* Seçilen ürünler — yıldız ile varsayılan seçenek belirlenir */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {selectedOptionalItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {tt('Henüz ürün seçilmedi. "Ürün Seç" ile ekleyin.', 'No products selected yet. Add them with "Select Products".')}
              </Typography>
            ) : (
              selectedOptionalItems.map((option, selectedIndex) => (
                <Box
                  key={option.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: defaultOptionIndex === selectedIndex ? 'secondary.50' : 'transparent',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.stockCode} • {option.unit}
                      {defaultOptionIndex === selectedIndex && ` • ${tt('Varsayılan', 'Default')}`}
                    </Typography>
                  </Box>
                  <Tooltip title={tt('Varsayılan yap', 'Set as default')}>
                    <IconButton
                      size="small"
                      onClick={() => setDefaultOptionIndex(selectedIndex)}
                      sx={{
                        color: defaultOptionIndex === selectedIndex ? 'secondary.main' : 'grey.400'
                      }}
                    >
                      <StarIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={tt('Çıkar', 'Remove')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const newItems = selectedOptionalItems.filter(i => i.id !== option.id);
                        setSelectedOptionalItems(newItems);
                        // Varsayılan silinen satırın gerisinde kalmasın
                        if (defaultOptionIndex >= newItems.length) {
                          setDefaultOptionIndex(Math.max(0, newItems.length - 1));
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))
            )}
          </Box>

          {/* Seçilen Ürün Sayısı */}
          {selectedOptionalItems.length > 0 && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'secondary.50', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="secondary.main" fontWeight="bold">
                ✓ {selectedOptionalItems.length} {tt('ürün seçildi', 'products selected')}
              </Typography>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setSelectedOptionalItems([]);
                  setDefaultOptionIndex(0);
                }}
              >
                {tt('Temizle', 'Clear')}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowOptionalItemsModal(false);
            setOptionGroupName('');
            setSelectedOptionalItems([]);
            setDefaultOptionIndex(0);
          }}>
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleAddOptionalItems}
            disabled={addingOptionalItems || !optionGroupName.trim() || selectedOptionalItems.length === 0}
            startIcon={addingOptionalItems ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {addingOptionalItems ? tt('Ekleniyor...', 'Adding...') : `${selectedOptionalItems.length} ${tt('Ürün Ekle', 'Add Product')}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ekstra Opsiyon Ekleme Modalı */}
      <Dialog
        open={showExtraOptionsModal}
        onClose={() => {
          setShowExtraOptionsModal(false);
          setExtraOptionItems([]);
          setSearchInputValue('');
          setSelectedMenuItemForAction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon color="info" />
          {tt('Ekstra Opsiyon Ekle', 'Add Extra Option')}
          {selectedMenuItemForAction && (
            <Chip 
              label={selectedMenuItemForAction.itemName} 
              size="small" 
              color="primary" 
              sx={{ ml: 1 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt('Boy/upgrade seçenekleri. Örn: Küçük Cola → Büyük Cola (+20₺)', 'Size/upgrade options. Ex: Small Cola → Large Cola (+20₺)')}
          </Alert>

          {/* ✅ Mevcut Ekstra Opsiyonlar */}
          {existingExtraOptions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {tt('Mevcut Ekstra Opsiyonlar', 'Existing Extra Options')} ({existingExtraOptions.length})
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'success.light', borderRadius: 1, overflow: 'hidden' }}>
                {existingExtraOptions.map((eo) => (
                  <Box 
                    key={eo.id}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      p: 1, 
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'success.50',
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <CheckCircleIcon fontSize="small" color="success" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{eo.optionItemName || tt('İsimsiz', 'Unnamed')}</Typography>
                      <Typography variant="caption" color="text.secondary">{eo.optionItemUnit || '-'}</Typography>
                    </Box>
                    <Chip 
                      label={`+${eo.priceDifference.toLocaleString(localeCode)} ₺`}
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                    <IconButton 
                      size="small" 
                      color="error"
                      disabled={deletingExtraOptionId === eo.id}
                      onClick={() => handleDeleteExtraOption(eo.id)}
                    >
                      {deletingExtraOptionId === eo.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {tt('Yeni Ekstra Opsiyon Ekle', 'Add New Extra Option')}
          </Typography>

          <TextField
            label={tt('Ürün Ara', 'Search Product')}
            placeholder={tt('Ürün adı veya stok kodu...', 'Product name or stock code...')}
            fullWidth
            size="small"
            value={searchInputValue}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            InputProps={{
              endAdornment: searchLoading ? <CircularProgress size={18} /> : null
            }}
            sx={{ mb: 1 }}
          />

          {/* Arama Sonuçları - Checkbox Liste */}
          <Box 
            sx={{ 
              maxHeight: 250, 
              overflow: 'auto', 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50',
              mb: 2
            }}
          >
            {searchOptions.length === 0 && !searchLoading && searchInputValue.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {tt('Sonuç bulunamadı', 'No results found')}
              </Typography>
            )}
            {searchOptions.length === 0 && searchInputValue.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {tt('Aramak için yazın...', 'Type to search...')}
              </Typography>
            )}
            {searchOptions.map((option) => {
              const isSelected = extraOptionItems.some(eo => eo.item.id === option.id);
              return (
                <Box
                  key={option.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isSelected ? 'info.50' : 'transparent',
                    '&:hover': { bgcolor: isSelected ? 'info.100' : 'grey.100' },
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (isSelected) {
                      setExtraOptionItems(extraOptionItems.filter(eo => eo.item.id !== option.id));
                    } else {
                      setExtraOptionItems([...extraOptionItems, { item: option, priceDifference: '0' }]);
                    }
                  }}
                >
                  <Checkbox 
                    checked={isSelected}
                    size="small"
                    color="info"
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.stockCode} • {option.unit}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Seçilen Ürünler ve Fiyat Farkı */}
          {extraOptionItems.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="info.main" fontWeight="bold">
                  ✓ {extraOptionItems.length} {tt('ürün seçildi', 'products selected')}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setExtraOptionItems([])}
                >
                  {tt('Temizle', 'Clear')}
                </Button>
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                {extraOptionItems.map((eo, index) => (
                  <Box 
                    key={eo.item.id}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      p: 1, 
                      borderBottom: index < extraOptionItems.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: 'white'
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{eo.item.name}</Typography>
                    </Box>
                    <TextField
                      type="text"
                      size="small"
                      value={eo.priceDifference}
                      onChange={(e) => {
                        const newItems = [...extraOptionItems];
                        newItems[index].priceDifference = e.target.value;
                        setExtraOptionItems(newItems);
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">+</InputAdornment>,
                        endAdornment: <InputAdornment position="end">₺</InputAdornment>,
                      }}
                      sx={{ width: 110 }}
                    />
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => setExtraOptionItems(extraOptionItems.filter((_, i) => i !== index))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowExtraOptionsModal(false);
            setExtraOptionItems([]);
            setSearchInputValue('');
            setSelectedMenuItemForAction(null);
          }}>
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleAddExtraOptions}
            disabled={addingExtraOptions || extraOptionItems.length === 0}
            startIcon={addingExtraOptions ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {addingExtraOptions ? tt('Ekleniyor...', 'Adding...') : `${extraOptionItems.length} ${tt('Ekstra Opsiyon Ekle', 'Add Extra Option')}`}
          </Button>
        </DialogActions>
      </Dialog>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {menu && (
          <Button
            color="error"
            onClick={openDeleteMenuDialog}
            disabled={saving}
          >
            {tt('Menüyü Sil', 'Delete Menu')}
          </Button>
        )}
        {menu && menu.menuItems && menu.menuItems.length > 0 && (
          <Button
            color="primary"
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            {tt('Yazdır', 'Print')}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="inherit">
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>

      {/* ✅ Ürün Silme Onay Dialog'u */}
      <Dialog
        open={deleteItemDialogOpen}
        onClose={() => setDeleteItemDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {tt('Ürün Silme Onayı', 'Product Delete Confirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {tt('Bu ürünü menüden silmek istediğinize emin misiniz?', 'Are you sure you want to delete this product from the menu?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteItemDialogOpen(false)} 
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteItem} 
            color="error" 
            variant="contained"
          >
            {tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Menü Silme Onay Dialog'u */}
      <Dialog
        open={deleteMenuDialogOpen}
        onClose={() => setDeleteMenuDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon color="error" />
          {tt('Menü Silme Onayı', 'Menu Delete Confirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {tt(
              `"${menu?.menuName || item?.name}" menüsünü tamamen silmek istediğinize emin misiniz?`,
              `Are you sure you want to completely delete the "${menu?.menuName || item?.name}" menu?`
            )}
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {tt('Bu işlem geri alınamaz! Tüm ürünler ve ayarlar silinecektir.', 'This action cannot be undone! All products and settings will be deleted.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteMenuDialogOpen(false)} 
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteMenu} 
            color="error" 
            variant="contained"
            disabled={saving}
          >
            {tt('Menüyü Sil', 'Delete Menu')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Gelişmiş Ürün Seçim Modalı (Autocomplete yerine) */}
      <InventoryProductSelectionModal
        open={isProductSelectOpen}
        onClose={() => setIsProductSelectOpen(false)}
        title={tt('Menüye Ürün Seç', 'Select Products for Menu')}
        warehouses={warehouses}
        categories={categories}
        excludeMenuItems // Menü içine menü eklenemez
        excludeItemIds={[
          ...(item ? [item.id] : []),
          ...(menu?.menuItems?.map(mi => mi.itemId) || []),
          ...pendingItems.map(p => p.item.id),
        ]}
        onSelectProducts={handleProductsSelected}
      />

      {/* ✅ Opsiyonlu ürün seçimi — "Ürün Ekle" ile aynı gelişmiş modal */}
      <InventoryProductSelectionModal
        open={isOptionalProductSelectOpen}
        onClose={() => setIsOptionalProductSelectOpen(false)}
        title={tt('Opsiyon Grubuna Ürün Seç', 'Select Products for Option Group')}
        warehouses={warehouses}
        categories={categories}
        excludeMenuItems // Menü içine menü eklenemez
        excludeItemIds={[
          ...(item ? [item.id] : []),
          ...(menu?.menuItems?.map(mi => mi.itemId) || []),
          ...selectedOptionalItems.map(o => o.id),
        ]}
        onSelectProducts={handleOptionalProductsSelected}
      />

      {/* ✅ Taslak Olarak Kaydet */}
      <Dialog
        open={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarkAddIcon color="success" />
          {tt('Taslak Olarak Kaydet', 'Save as Template')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt(
              'Taslak bu bilgisayarın tarayıcısına kaydedilir; başka bilgisayarda görünmez.',
              'The template is saved in this computer\'s browser; it will not appear on another computer.'
            )}
          </Alert>
          <TextField
            autoFocus
            fullWidth
            size="small"
            required
            label={tt('Taslak Adı', 'Template Name')}
            placeholder={tt('Örn: Standart Menü İçeriği', 'Ex: Standard Menu Content')}
            value={templateNameInput}
            onChange={(e) => setTemplateNameInput(e.target.value)}
            helperText={tt(
              'Aynı adı verirsen mevcut taslağın üzerine yazılır.',
              'Using an existing name overwrites that template.'
            )}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            {tt('Kaydedilecek içerik', 'Content to be saved')}:{' '}
            <strong>
              {(menu?.menuItems || []).filter(mi => !mi.isOptional).length} {tt('ürün', 'products')}
            </strong>
            {' • '}
            <strong>
              {(menu?.menuItems || []).filter(mi => mi.isOptional).length} {tt('opsiyonlu satır', 'optional rows')}
            </strong>
            {' • '}
            <strong>
              {(menu?.menuItems || []).reduce((sum, mi) => sum + (mi.extraOptions?.length || 0), 0)}{' '}
              {tt('ekstra opsiyon', 'extra options')}
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveTemplateModal(false)}>
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            onClick={handleSaveTemplate}
            disabled={!templateNameInput.trim()}
          >
            {tt('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Taslaktan Uygula */}
      <Dialog
        open={showApplyTemplateModal}
        onClose={() => (applyingTemplateId ? undefined : setShowApplyTemplateModal(false))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BookmarksIcon color="success" />
          {tt('Taslaktan Uygula', 'Apply Template')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt(
              'Taslaktaki ürünler bu menüye eklenir. Menüde zaten olan ürünler atlanır, mevcut satırlar silinmez.',
              'Products in the template are added to this menu. Products already in the menu are skipped and existing rows are not deleted.'
            )}
          </Alert>

          {applyReport && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {applyReport}
            </Alert>
          )}

          {templates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {tt('Kayıtlı taslak yok.', 'No saved templates.')}
            </Typography>
          ) : (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {templates.map((template, index) => (
                <Box
                  key={template.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderTop: index === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {countTemplateRows(template)} {tt('satır', 'rows')}
                      {template.optionGroups.length > 0 &&
                        ` • ${template.optionGroups.length} ${tt('opsiyon grubu', 'option groups')}`}
                      {template.sourceItemName && ` • ${template.sourceItemName}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" noWrap>
                      {[
                        ...template.items.map(i => i.itemName),
                        ...template.optionGroups.flatMap(g => g.items.map(i => i.itemName)),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                  <Tooltip title={tt('Bu menüye uygula', 'Apply to this menu')}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={
                          applyingTemplateId === template.id
                            ? <CircularProgress size={14} color="inherit" />
                            : <ContentPasteIcon />
                        }
                        onClick={() => handleApplyTemplate(template)}
                        disabled={applyingTemplateId !== null}
                      >
                        {applyingTemplateId === template.id
                          ? tt('Uygulanıyor...', 'Applying...')
                          : tt('Uygula', 'Apply')}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title={tt('Taslağı sil', 'Delete template')}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTemplate(template.id)}
                        disabled={applyingTemplateId !== null}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowApplyTemplateModal(false)}
            disabled={applyingTemplateId !== null}
          >
            {tt('Kapat', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
